/**
 * RelatedPosts — sidebar/footer panel rendering up to 3 related posts.
 *
 * The selection algorithm is intentionally simple: caller passes the
 * full `candidates` array; the component drops the current post (by
 * slug) and renders the first `max` remaining entries. Sophisticated
 * scoring (category overlap, tag overlap) belongs at the service layer.
 *
 * @module @puffin-core/marketing-shell/blog/RelatedPosts
 */

import PropTypes from 'prop-types';
import BlogPostCard from './BlogPostCard.jsx';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';

export default function RelatedPosts({
  candidates,
  currentSlug,
  max = 3,
  heading: hostHeading,
  emptyContent = null,
  hrefPrefix = '/blog',
  readingMinutesLabel,
}) {
  // The heading (which also names the region): the host's, else the catalog's in the reader's
  // language — never an English default parameter.
  const t = useMarketingShellTranslate();
  const heading = hostHeading ?? t('relatedPosts.heading');
  const filtered = (candidates || []).filter((p) => p && p.slug !== currentSlug);
  const shown = filtered.slice(0, max);

  if (shown.length === 0) {
    return emptyContent || null;
  }

  return (
    <aside className="post-related" aria-label={heading}>
      <h2 className="post-related-heading section-heading">{heading}</h2>
      <ul className="post-related-list">
        {shown.map((post) => (
          <li key={post.slug}>
            <BlogPostCard
              post={post}
              hrefPrefix={hrefPrefix}
              readingMinutesLabel={readingMinutesLabel}
            />
          </li>
        ))}
      </ul>
    </aside>
  );
}

RelatedPosts.propTypes = {
  candidates: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  currentSlug: PropTypes.string.isRequired,
  max: PropTypes.number,
  heading: PropTypes.string,
  emptyContent: PropTypes.node,
  hrefPrefix: PropTypes.string,
  readingMinutesLabel: PropTypes.string,
};
